import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SvgIconComponent } from '@mui/icons-material';
import { useRef, useState } from 'react';
import { SVGIconComponent } from '../../../icons/types';
import { StatusBadge } from '../../../status-badge/status-badge.component';
import { useCourierStatusBadgeStyles } from './courier-status-badge.styles';

export interface CourierStatusBadgeDecision {
  label: string;
  onSelect(): void;
}

export interface CourierStatusBadgeProps {
  label: string;
  color: string;
  Icon: SvgIconComponent | SVGIconComponent;
  /** The decisions offered. Empty renders the plain badge — what a viewer who may not decide sees. */
  decisions?: CourierStatusBadgeDecision[];
}

/**
 * A courier's status badge, doubling as the way into an approval decision.
 *
 * Wraps the shared `StatusBadge` rather than changing it: that one is read-only everywhere else.
 *
 * Choosing a decision opens the approval modal on it rather than sending it — a lifecycle change
 * should not be one mis-click away.
 */
export function CourierStatusBadge({ label, color, Icon, decisions = [] }: CourierStatusBadgeProps) {
  const classes = useCourierStatusBadgeStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // Measured, not hardcoded: the badge sets its own width and the menu sits under the whole control.
  const [menuMinWidth, setMenuMinWidth] = useState<number>();

  const toggle = () => {
    setMenuMinWidth(anchorRef.current?.offsetWidth);
    setOpen((wasOpen) => !wasOpen);
  };

  const badge = <StatusBadge color={color} label={label} cornerRounding="max" Icon={Icon} iconSize="18px" />;

  if (decisions.length === 0) {
    return badge;
  }

  const close = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <div
        ref={anchorRef}
        className={classes.trigger}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
      >
        {badge}
        <KeyboardArrowDownIcon className={classes.chevron} />
      </div>

      <Popper open={open} anchorEl={anchorRef.current} style={{ zIndex: 999 }} placement="bottom-start" transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top' }}>
            <Paper style={{ minWidth: menuMinWidth }}>
              <ClickAwayListener onClickAway={close}>
                <MenuList
                  autoFocusItem={open}
                  onKeyDown={(event) => {
                    if (event.key === 'Tab' || event.key === 'Escape') {
                      event.preventDefault();
                      setOpen(false);
                    }
                  }}
                >
                  {decisions.map((decision) => (
                    <MenuItem
                      key={decision.label}
                      onClick={() => {
                        setOpen(false);
                        decision.onSelect();
                      }}
                    >
                      {decision.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
